import { defineStore } from 'pinia'
import axios from 'axios'
import { getToken, setToken, removeToken, removeUserInfo, clearAuth } from '@/utils/auth'
import { login as apiLogin, logout as apiLogout } from '@/api/auth'
import { getCurrentUser } from '@/api/users'

const ADMIN_ROLES = new Set(['ADMIN', 'SUPER_ADMIN', 'admin'])

const parseStoredUser = () => {
  try {
    const raw = localStorage.getItem('user_info') || localStorage.getItem('user') || '{}'
    const parsed = JSON.parse(raw)
    return parsed?.id ? parsed : {}
  } catch {
    return {}
  }
}

const buildPermissions = (role: string) => {
  const permissions = ['view_dashboard']
  if (role === 'ADMIN') {
    permissions.push(
      'manage_users', 'manage_roles', 'manage_tenants', 'manage_system_settings',
      'view_all_data', 'edit_all_data', 'delete_all_data'
    )
  }
  if (role === 'ADMIN' || role === 'SALES') {
    permissions.push('manage_customers', 'manage_opportunities', 'manage_contacts', 'view_reports')
  }
  if (role === 'BD') {
    permissions.push('view_assigned_opportunities', 'edit_assigned_opportunities', 'manage_followups')
  }
  return permissions
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: parseStoredUser(),
    permissions: [] as string[],
    isInitialized: false,
    currentTenantId: Number(localStorage.getItem('current_tenant_id')) || null,
    lastValidationTime: 0,
    isValidating: false,
  }),

  getters: {
    /** 始终从 localStorage/cookie 读取，与 setToken 保持同步 */
    token: () => getToken() || '',
    isAuthenticated: () => !!getToken(),
    getUser: (state) => state.user,
    userInfo: (state) => (state.user?.id ? state.user : null),
    isAdmin: (state) =>
      Boolean(
        state.user?.is_admin ||
        ADMIN_ROLES.has(state.user?.role) ||
        state.user?.username === 'admin'
      ),
    userId: (state) => state.user?.id || null,
    id: (state) => state.user?.id || null,
    name: (state) => state.user?.name || state.user?.username || '',
    username: (state) => state.user?.username || '',
    email: (state) => state.user?.email || '',
    role: (state) => state.user?.role || '',
    avatar: (state) => state.user?.avatar || '',
    tenant: (state) => state.user?.tenant || null,
    getTenantId: (state) => state.user?.tenant?.id || state.user?.current_tenant_id || state.currentTenantId,
    getTenantCode: (state) => state.user?.tenant?.code || '',
    getTenantName: (state) => state.user?.tenant?.name || '',
  },

  actions: {
    initUserInfo() {
      if (this.isInitialized) return
      const stored = parseStoredUser()
      if (stored?.id) {
        this.setUserInfo(stored)
      } else {
        this.user = {}
        this.permissions = []
      }
      this.isInitialized = true
    },

    setUserInfo(userInfo: Record<string, any>) {
      if (!userInfo?.id) return

      const oldUserId = this.user?.id
      if (oldUserId && userInfo.id !== oldUserId) {
        ;['user_info', 'user', 'user_id', 'current_user'].forEach((key) => localStorage.removeItem(key))
      }

      const normalized = {
        ...userInfo,
        id: userInfo.id || null,
        current_tenant_id: userInfo.current_tenant_id || userInfo.tenant?.id || null,
        tenant: userInfo.tenant || this.user?.tenant || null,
      }

      if (normalized.is_admin || ADMIN_ROLES.has(normalized.role)) {
        normalized.is_admin = true
      }

      this.user = normalized
      this.permissions = buildPermissions(normalized.role || '')
      this.currentTenantId = normalized.current_tenant_id || normalized.tenant?.id || null

      localStorage.setItem('user_info', JSON.stringify(normalized))
      localStorage.setItem('user', JSON.stringify(normalized))
      if (normalized.id) localStorage.setItem('user_id', String(normalized.id))
      if (normalized.tenant) {
        localStorage.setItem('current_tenant_id', String(normalized.tenant.id))
        localStorage.setItem('current_tenant', JSON.stringify(normalized.tenant))
      } else if (normalized.current_tenant_id) {
        localStorage.setItem('current_tenant_id', String(normalized.current_tenant_id))
      }
      if (normalized.tenant) {
        localStorage.setItem('last_tenant_id', String(normalized.tenant.id))
        localStorage.setItem('last_tenant_code', normalized.tenant.code || '')
      }
    },

    clearUserInfo() {
      this.user = {}
      this.permissions = []
      this.currentTenantId = null
      this.isInitialized = false
      ;[
        'user_info', 'user', 'user_id', 'user_role', 'current_tenant_id', 'current_tenant',
        'last_tenant_id', 'last_tenant_code', 'user_info_set_time', 'login_source',
        'token_source', 'login_time', 'session_id',
      ].forEach((key) => localStorage.removeItem(key))
    },

    setCurrentTenantId(tenantId: number | string | null) {
      const id = tenantId == null ? null : Number(tenantId)
      this.currentTenantId = id
      if (this.user && id) {
        this.user = { ...this.user, current_tenant_id: id }
        localStorage.setItem('current_tenant_id', String(id))
        localStorage.setItem('user_info', JSON.stringify(this.user))
        localStorage.setItem('user', JSON.stringify(this.user))
      }
    },

    hasPermission(permission: string) {
      return this.permissions.includes(permission)
    },

    async loginWithPayload(loginData: Record<string, any>) {
      const data = await apiLogin(loginData)
      if (!data?.access_token) {
        throw new Error('登录失败：未获取到有效的访问令牌')
      }
      const userInfo = data.user || data.user_info
      if (userInfo) this.setUserInfo(userInfo)
      setToken(data.access_token)
      localStorage.setItem('token_source', 'password_login')
      localStorage.setItem('login_time', new Date().toISOString())
      if (data.session_id) localStorage.setItem('session_id', data.session_id)
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`
      return data
    },

    async getUserInfo() {
      try {
        const data = await getCurrentUser()
        if (data && !data.tenant && data.current_tenant_id) {
          try {
            const tenantInfo = JSON.parse(localStorage.getItem('current_tenant') || 'null')
            if (tenantInfo?.id === data.current_tenant_id) data.tenant = tenantInfo
          } catch { /* ignore */ }
        }
        this.setUserInfo(data)
        return data
      } catch {
        const cached = parseStoredUser()
        if (cached?.id) {
          this.setUserInfo(cached)
          return cached
        }
        return null
      }
    },

    async login(email: string, password: string) {
      try {
        const formData = new FormData()
        formData.append('username', email)
        formData.append('password', password)

        const response = await axios.post('/api/v1/auth/login', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        const { access_token, user_info, session_id } = response.data
        if (!user_info?.is_active) {
          this.resetToken()
          return false
        }

        setToken(access_token)
        this.setUserInfo(user_info)
        if (session_id) localStorage.setItem('session_id', session_id)
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
        return true
      } catch (error) {
        console.error('Login failed:', error)
        this.resetToken()
        return false
      }
    },

    async logout() {
      const sessionId = localStorage.getItem('session_id')
      try {
        if (sessionId) {
          await axios.post('/api/v1/auth/logout', {}, { headers: { 'X-Session-Id': sessionId } })
        } else {
          await apiLogout()
        }
      } catch (error) {
        console.warn('登出接口调用失败:', error)
      } finally {
        this.clearUserInfo()
        clearAuth()
        removeToken()
        removeUserInfo()
        delete axios.defaults.headers.common['Authorization']
      }
    },

    resetToken() {
      this.clearUserInfo()
      removeToken()
      removeUserInfo()
      delete axios.defaults.headers.common['Authorization']
    },

    async validateToken() {
      const publicPages = ['/login', '/register', '/reset-password', '/forgot-password', '/activate', '/reset-pwd']
      const currentUrl = window.location.pathname
      if (publicPages.some((page) => currentUrl.startsWith(page))) return false

      const now = Date.now()
      if (now - this.lastValidationTime < 30000) return true

      if (this.isValidating) {
        return new Promise<boolean>((resolve) => {
          const checkInterval = setInterval(() => {
            if (!this.isValidating) {
              clearInterval(checkInterval)
              resolve(!!getToken())
            }
          }, 100)
          setTimeout(() => {
            clearInterval(checkInterval)
            resolve(false)
          }, 5000)
        })
      }

      this.isValidating = true
      this.lastValidationTime = now

      const token = getToken()
      if (!token) {
        this.isValidating = false
        return false
      }

      try {
        const response = await axios.get('/api/v1/auth/current-user', {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': localStorage.getItem('current_tenant_id') || '',
          },
          timeout: 15000,
          // @ts-expect-error custom axios flag
          _isTokenValidation: true,
        })

        if (response.data?.id) {
          setToken(token)
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          this.setUserInfo(response.data)
          this.isValidating = false
          return true
        }
      } catch (error) {
        this.isValidating = false
        if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
          this.resetToken()
          return false
        }
        return !!token
      }

      this.isValidating = false
      return false
    },

    async refreshToken() {
      try {
        const response = await axios.post('/api/v1/auth/refresh-token')
        if (response.data?.access_token) {
          setToken(response.data.access_token)
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`
          return true
        }
        throw new Error('刷新token返回数据格式不正确')
      } catch (error) {
        this.resetToken()
        throw error
      }
    },

    async initAuth() {
      const token = getToken()
      if (!token) return false
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      this.initUserInfo()
      const isValid = await this.validateToken()
      if (!isValid) {
        const publicPages = ['/login', '/register', '/reset-password', '/forgot-password', '/activate', '/reset-pwd']
        if (publicPages.some((page) => window.location.pathname.startsWith(page))) {
          return true
        }
        this.resetToken()
        return false
      }
      return true
    },
  },
})

/** 兼容旧 useUserStore 引用 */
export const useUserStore = useAuthStore
